import React, { useState } from "react";
import { Button, Input } from "reactstrap";
import "./resellerStudio.css";
import type {
  CatalogueLinkExtensionRequest,
  CatalogueShareLink,
  CatalogueViewerGate,
} from "../../types/catalogue.types";
import { CATALOGUE_PRICE_PRESETS } from "./cataloguePricing";
import { formatSharePhoneDisplay } from "./catalogueShareCountries";
import { VIEWER_GATE_OPTIONS, VIEWER_GATE_SHORT } from "./catalogueViewerGate";
import { SHARE_EXPIRY_UNITS, type ShareExpiryUnit } from "./shareLinkExpiry";

/** Tiers a link can show: the presets, plus a custom tier the link already uses. "" = default prices. */
export const linkTierOptions = (current?: string) => {
  const presets = CATALOGUE_PRICE_PRESETS.filter((t) => t !== "DEFAULT");
  const own = current && !presets.includes(current as (typeof presets)[number]) && current !== "DEFAULT" ? [current] : [];
  return ["", ...presets, ...own];
};

/**
 * Studio links can switch; reseller links always need a code, and client links are the reseller's.
 * An open link made without a mobile can't become exclusive: nobody could verify.
 */
export const canSwitchAccess = (link: CatalogueShareLink, resellerOn: boolean) =>
  resellerOn && (!link.linkRole || link.linkRole === "DIRECT") && !(link.accessMode === "OPEN" && !link.recipientPhone);

/** How this one link opens. Other links on the catalogue may differ. */
export const ShareLinkAccessBadge: React.FC<{ link: CatalogueShareLink }> = ({ link }) => {
  if (link.linkRole === "RESELLER") return <span className="hkp-link-access is-reseller">Reseller</span>;
  if (link.accessMode === "OPEN") return <span className="hkp-link-access is-open">Open</span>;
  return (
    <span className="hkp-link-access is-exclusive">
      Exclusive{link.requirePassword ? <small> · password</small> : null}
    </span>
  );
};

export type ShareLinkStatus = "active" | "expired" | "disabled";

export function shareLinkStatus(link: CatalogueShareLink, nowMs = Date.now()): ShareLinkStatus {
  if (link.disabled) return "disabled";
  if (link.expiresAt && new Date(link.expiresAt).getTime() < nowMs) return "expired";
  return "active";
}

/** One short line under the badge: what the link asks and which prices it shows, only when not the default. */
export function shareLinkHowLine(link: CatalogueShareLink, viewerGateOn: boolean): string {
  const parts: string[] = [];
  if (viewerGateOn && link.accessMode === "OPEN" && link.viewerGate && link.viewerGate !== "NONE") {
    parts.push(VIEWER_GATE_SHORT[link.viewerGate]);
  }
  if (link.priceTier && link.priceTier !== "DEFAULT") parts.push(`${link.priceTier} prices`);
  return parts.join(" · ");
}

export interface ShareLinkRowProps {
  link: CatalogueShareLink;
  url: string;
  copied: boolean;
  expanded: boolean;
  resellerOn: boolean;
  viewerGateOn: boolean;
  busy: { access?: boolean; gate?: boolean; tier?: boolean; expiry?: boolean };
  formatDate: (iso?: string) => string;
  onCopy: () => void;
  onToggle: () => void;
  onSwitchAccess: () => void;
  onGate: (gate: CatalogueViewerGate) => void;
  onTier: (tier: string) => void;
  onPassword: () => void;
  onEmail: () => void;
  onExtend: () => void;
  /** Resolves true when saved, so the row can close its editor. */
  onSetExpiry: (amount: number, unit: ShareExpiryUnit) => Promise<boolean>;
  onDisable: () => void;
  /** A buyer asked, on this expired link, for it to reopen. */
  extensionRequest?: CatalogueLinkExtensionRequest;
  extensionBusy?: boolean;
  onApproveExtension?: (days: 7 | 30 | 90) => void;
  onDeclineExtension?: () => void;
}

export const EXTENSION_DAYS: Array<7 | 30 | 90> = [7, 30, 90];

/**
 * One shared link: who it is for, how it opens and until when, with Copy right there. Everything
 * else (access, prices, expiry, password, email, disable) waits behind Manage so the list stays
 * readable on a phone.
 */
const ShareLinkRow: React.FC<ShareLinkRowProps> = ({
  link,
  url,
  copied,
  expanded,
  resellerOn,
  viewerGateOn,
  busy,
  formatDate,
  onCopy,
  onToggle,
  onSwitchAccess,
  onGate,
  onTier,
  onPassword,
  onEmail,
  onExtend,
  onSetExpiry,
  onDisable,
  extensionRequest,
  extensionBusy,
  onApproveExtension,
  onDeclineExtension,
}) => {
  const [expiryAmount, setExpiryAmount] = useState(3);
  const [expiryUnit, setExpiryUnit] = useState<ShareExpiryUnit>("days");
  const status = shareLinkStatus(link);
  const live = status === "active";
  const name = link.recipientName?.trim() || "Open link";
  const contact = [
    link.recipientPhone ? formatSharePhoneDisplay(link.recipientPhone, link.recipientCountryCode) : "",
    link.recipientEmail || "",
  ]
    .filter(Boolean)
    .join(" · ");
  const how = shareLinkHowLine(link, viewerGateOn);
  const when =
    status === "disabled"
      ? `Disabled ${formatDate(link.disabledAt)}`
      : status === "expired"
      ? `Expired ${formatDate(link.expiresAt)}`
      : `Until ${formatDate(link.expiresAt)}`;
  const panelId = `hkp-link-manage-${link.id}`;
  const canGate = viewerGateOn && link.accessMode === "OPEN" && (!link.linkRole || link.linkRole === "DIRECT");
  const canTier = resellerOn && link.linkRole !== "CLIENT";

  return (
    <div className={`hkp-link-row is-${status}${expanded ? " is-expanded" : ""}`} data-testid="share-link-row">
      <div className="hkp-link-row-main">
        <div className="hkp-link-who">
          <strong title={url}>{name}</strong>
          {contact ? <small>{contact}</small> : null}
        </div>
        <div className="hkp-link-how">
          <ShareLinkAccessBadge link={link} />
          {how ? <small>{how}</small> : null}
        </div>
        <div className={`hkp-link-when${live ? "" : " is-off"}`}>{when}</div>
        <div className="hkp-link-do">
          {live ? (
            <Button type="button" className="btn-cat-action btn-cat-open" onClick={onCopy}>
              {copied ? "Copied" : "Copy"}
            </Button>
          ) : null}
          <button
            type="button"
            className={`hkp-link-manage-toggle${expanded ? " is-open" : ""}`}
            aria-expanded={expanded}
            aria-controls={panelId}
            onClick={onToggle}
          >
            Manage
            <span className="hkp-link-caret" aria-hidden="true" />
          </button>
        </div>
      </div>

      {extensionRequest && status === "expired" ? (
        <div className="hkp-link-ext" data-testid="link-extension-ask" role="group" aria-label="Extension request">
          <p className="hkp-link-ext-who">
            <strong>{extensionRequest.requesterName}</strong>
            {extensionRequest.nameFromLink ? "" : " (name typed on the link)"} asked to reopen this link ·{" "}
            {formatDate(extensionRequest.requestedAt)}
          </p>
          <div className="hkp-link-controls">
            {EXTENSION_DAYS.map((d) => (
              <Button
                key={d}
                type="button"
                className="btn-cat-action btn-cat-open"
                disabled={extensionBusy}
                onClick={() => onApproveExtension?.(d)}
              >
                {d} days
              </Button>
            ))}
            <Button type="button" className="btn-cat-action btn-cat-edit" disabled={extensionBusy} onClick={onDeclineExtension}>
              Decline
            </Button>
          </div>
        </div>
      ) : null}

      {expanded ? (
        <div className="hkp-link-manage" id={panelId}>
          {status === "disabled" ? (
            <p className="hkp-link-note">This link is disabled. Share a new link if they should see the catalogue again.</p>
          ) : (
            <>
              <div className="hkp-link-field">
                <span className="hkp-link-label">Who can open it</span>
                <div className="hkp-link-controls">
                  {canSwitchAccess(link, resellerOn) ? (
                    <Button type="button" className="btn-cat-action btn-cat-edit" disabled={busy.access} onClick={onSwitchAccess}>
                      {link.accessMode === "OPEN" ? "Make exclusive" : "Make open"}
                    </Button>
                  ) : null}
                  {canGate ? (
                    <Input
                      type="select"
                      bsSize="sm"
                      aria-label={`What ${name} asks viewers`}
                      value={link.viewerGate || "NONE"}
                      disabled={busy.gate}
                      onChange={(e) => onGate(e.target.value as CatalogueViewerGate)}
                    >
                      {VIEWER_GATE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.title}
                        </option>
                      ))}
                    </Input>
                  ) : null}
                  {link.accessMode !== "OPEN" ? (
                    <Button type="button" className="btn-cat-action btn-cat-edit" onClick={onPassword}>
                      Password
                    </Button>
                  ) : null}
                </div>
              </div>

              {canTier ? (
                <div className="hkp-link-field">
                  <span className="hkp-link-label">Prices they see</span>
                  <div className="hkp-link-controls">
                    <Input
                      type="select"
                      bsSize="sm"
                      aria-label={`Prices ${name} sees`}
                      value={link.priceTier === "DEFAULT" ? "" : link.priceTier || ""}
                      disabled={busy.tier}
                      onChange={(e) => onTier(e.target.value)}
                    >
                      {linkTierOptions(link.priceTier).map((t) => (
                        <option key={t || "default"} value={t}>
                          {t || "Default"}
                        </option>
                      ))}
                    </Input>
                  </div>
                </div>
              ) : null}

              <div className="hkp-link-field">
                <span className="hkp-link-label">Expires</span>
                <div className="hkp-link-controls" data-testid="link-expiry-editor">
                  <Input
                    type="number"
                    bsSize="sm"
                    min={1}
                    step={1}
                    className="hkp-link-num"
                    aria-label="Expires in"
                    value={expiryAmount > 0 ? expiryAmount : ""}
                    onChange={(e) => setExpiryAmount(Math.floor(Number(e.target.value)) || 0)}
                  />
                  <Input
                    type="select"
                    bsSize="sm"
                    className="hkp-link-unit"
                    aria-label="Expiry unit"
                    value={expiryUnit}
                    onChange={(e) => setExpiryUnit(e.target.value as ShareExpiryUnit)}
                  >
                    {SHARE_EXPIRY_UNITS.map((u) => (
                      <option key={u.value} value={u.value}>
                        {u.label}
                      </option>
                    ))}
                  </Input>
                  <Button
                    type="button"
                    className="btn-cat-action btn-cat-open"
                    disabled={busy.expiry}
                    onClick={() => void onSetExpiry(expiryAmount, expiryUnit)}
                  >
                    {busy.expiry ? "Saving…" : "Set from now"}
                  </Button>
                  <Button type="button" className="btn-cat-action btn-cat-edit" disabled={busy.expiry} onClick={onExtend}>
                    +7 days
                  </Button>
                </div>
                <small className="hkp-link-hint">Counted from now. Shorter or longer than it is today.</small>
              </div>

              <div className="hkp-link-field">
                <span className="hkp-link-label">Send again</span>
                <div className="hkp-link-controls">
                  <Button type="button" className="btn-cat-action btn-cat-duplicate" disabled={!link.recipientEmail} onClick={onEmail}>
                    Email the link
                  </Button>
                  {!link.recipientEmail ? <small className="hkp-link-hint">No email on this link. Copy it and send it yourself.</small> : null}
                </div>
              </div>

              <div className="hkp-link-field is-danger">
                <Button type="button" className="btn-cat-action btn-cat-delete" onClick={onDisable}>
                  Disable link
                </Button>
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default ShareLinkRow;
